import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { useBilling } from "@/context/BillingContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling");
  const [plan, setPlan] = useState(null);
  const { refresh } = useBilling();
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      attempts.current += 1;
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        if (r.data.payment_status === "paid") { setPlan(r.data.plan); setState("paid"); await refresh({ sync: true }); return; }
        if (r.data.status === "expired") { setState("expired"); return; }
      } catch (e) { console.warn("[apex] payment status:", e?.message || e); }
      if (attempts.current >= 8) { setState("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, refresh]);

  return (
    <div className="p-6 max-w-lg mx-auto min-h-[60vh] flex items-center" data-testid="payment-success-page">
      <div className="apx-card p-8 w-full text-center space-y-4">
        {state === "polling" && <><Loader2 className="w-10 h-10 mx-auto animate-spin text-cyan-300" /><h1 className="font-display text-2xl font-bold">Confirming your subscription…</h1><p className="text-sm text-slate-400">We're verifying the payment with Stripe. This usually takes a few seconds.</p></>}
        {state === "paid" && <><CheckCircle2 className="w-12 h-12 mx-auto text-emerald-400" /><h1 className="font-display text-2xl font-bold" data-testid="payment-success-title">Welcome to {plan === "premium" ? "Premium" : "Pro"}</h1><p className="text-sm text-slate-400">Your plan is active and paid features are unlocked across EV-IDEX. A receipt has been emailed by Stripe.</p>
          <div className="flex gap-2 justify-center pt-2"><Link to="/dashboard" className="btn-primary rounded-lg px-4 py-2 text-sm inline-flex items-center gap-2" data-testid="payment-success-dashboard"><Sparkles className="w-4 h-4" /> Go to dashboard</Link><Link to="/settings" className="btn-ghost rounded-lg px-4 py-2 text-sm" data-testid="payment-success-billing">Billing settings</Link></div></>}
        {(state === "timeout" || state === "expired" || state === "error") && <><XCircle className="w-12 h-12 mx-auto text-amber-400" /><h1 className="font-display text-2xl font-bold">We couldn't confirm the payment yet</h1><p className="text-sm text-slate-400">{state === "expired" ? "This checkout session expired before payment was completed." : "If you were charged, your plan will update automatically within a minute via Stripe's webhook. You can also refresh your billing status in Settings."}</p>
          <div className="flex gap-2 justify-center pt-2"><Link to="/settings?billing=refresh" className="btn-primary rounded-lg px-4 py-2 text-sm">Check billing status</Link><Link to="/pricing" className="btn-ghost rounded-lg px-4 py-2 text-sm">Back to plans</Link></div></>}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="p-6 max-w-lg mx-auto min-h-[60vh] flex items-center" data-testid="payment-cancel-page">
      <div className="apx-card p-8 w-full text-center space-y-4">
        <XCircle className="w-12 h-12 mx-auto text-slate-500" />
        <h1 className="font-display text-2xl font-bold">Checkout cancelled</h1>
        <p className="text-sm text-slate-400">No charge was made. You're still on your current plan and can upgrade whenever you're ready.</p>
        <div className="flex gap-2 justify-center pt-2"><Link to="/pricing" className="btn-primary rounded-lg px-4 py-2 text-sm" data-testid="payment-cancel-pricing">View plans</Link><Link to="/dashboard" className="btn-ghost rounded-lg px-4 py-2 text-sm">Dashboard</Link></div>
      </div>
    </div>
  );
}
